import { useState } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Battery,
  Camera,
  Clock,
  Globe,
  Lock,
  MapPin,
  MessageCircle,
  MessageSquare,
  Mic,
  Monitor,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  Radio,
  Shield,
  Smartphone,
  Wifi,
} from "lucide-react";
import { VARIANT } from "@/constants/links";

const isVip = VARIANT === "vip";

// ── Dati di esempio (demo). Coerenti con le categorie reali del prodotto. ──
// Etichettati esplicitamente come dimostrativi in fondo al pannello.

const DEVICE = {
  child: "Sofia",
  device: "samsung SM-A528B · Android 14",
  battery: 73,
  lastSeen: "adesso",
  online: true,
};

const CHATS = [
  { app: "WhatsApp", name: "Gruppo Classe 2ªB", preview: "Marco: domani portate il libro di scienze?", time: "14:32", unread: 4, color: "#25D366", initial: "W" },
  { app: "WhatsApp", name: "Giulia", preview: "Ok ci vediamo alle 16 al parco 😊", time: "14:10", unread: 2, color: "#25D366", initial: "W" },
  { app: "WhatsApp", name: "Sconosciuto (+39 34• ••• ••21)", preview: "Ciao, ti ho scritto ieri su insta…", time: "13:48", unread: 1, color: "#25D366", initial: "W", flagged: true },
  { app: "TikTok", name: "leo.gaming", preview: "guarda questo video ti fa ridere", time: "12:55", unread: 0, color: "#111111", initial: "T" },
  { app: "Telegram", name: "Canale offerte", preview: "🔥 Sconto lampo fino a stasera", time: "11:20", unread: 12, color: "#2AABEE", initial: "✈" },
];

const CALLS = [
  { name: "Mamma", number: "+39 340 ••• ••12", dir: "in", time: "14:02", dur: "3:12" },
  { name: "Sconosciuto", number: "+39 34• ••• ••21", dir: "missed", time: "13:47", dur: "—" },
  { name: "Luca (compagno)", number: "+39 388 ••• ••74", dir: "out", time: "12:15", dur: "0:48" },
  { name: "Nonna", number: "+39 0784 •••1•3", dir: "in", time: "09:30", dur: "6:05" },
];

const SITES = [
  { url: "youtube.com", title: "Video — tutorial skate", dur: "42 min", ok: true },
  { url: "google.com/search", title: "ricerca: compito di scienze 2ª media", dur: "6 min", ok: true },
  { url: "sito-scommesse.example", title: "Pagina bloccata dalla regola", dur: "0 min", ok: false },
  { url: "wikipedia.org", title: "Rivoluzione industriale", dur: "11 min", ok: true },
];

const APPS = [
  { app: "WhatsApp", time: "1h 24m", w: "72%", color: "#25D366" },
  { app: "YouTube", time: "58m", w: "56%", color: "#FF0000" },
  { app: "TikTok", time: "41m", w: "44%", color: "#111111" },
  { app: "Roblox", time: "33m", w: "36%", color: "#7763E5" },
  { app: "Chrome", time: "18m", w: "22%", color: "#4285F4" },
];

const SMS = [
  { from: "TIM", text: "La tua offerta Giga 50 si rinnova domani.", time: "08:15" },
  { from: "+39 388 ••• ••74", text: "Sei a scuola? Ti aspetto al cancello", time: "07:52" },
];


const CATEGORIES = [
  { id: "chat", label: "Chat", sub: "WhatsApp & social", icon: MessageCircle, count: 5 },
  { id: "calls", label: "Chiamate", sub: "registro completo", icon: Phone, count: 4 },
  { id: "screen", label: "Screenshot", sub: "schermo & camera", icon: Monitor, count: 3 },
  { id: "location", label: "Posizione", sub: "GPS live", icon: MapPin, count: null },
  { id: "web", label: "Siti visitati", sub: "cronologia web", icon: Globe, count: 4 },
  { id: "apps", label: "Utilizzo app", sub: "tempo per app", icon: Activity, count: 5 },
  { id: "sms", label: "SMS", sub: "messaggi di testo", icon: MessageSquare, count: 2 },
  { id: "audio", label: "Audio ambiente", sub: "ascolto remoto", icon: Mic, count: null },
  { id: "commands", label: "Comandi live", sub: "controllo remoto", icon: Radio, count: null },
];

const DirIcon = ({ dir }) => {
  if (dir === "missed") return <PhoneMissed size={15} className="text-[#e8558f]" />;
  if (dir === "out") return <PhoneCall size={15} className="text-[#7763e5]" />;
  return <PhoneIncoming size={15} className="text-emerald-500" />;
};

const Panel = ({ id }) => {
  if (id === "chat")
    return (
      <div className="space-y-2.5">
        {CHATS.map((c) => (
          <div key={c.name} className={`rounded-2xl border bg-white p-3.5 ${c.flagged ? "border-[#e8558f]/50" : "border-[#1f1f25]/8"}`}>
            <div className="flex items-start gap-3">
              <span
                className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-black text-white"
                style={{ backgroundColor: c.color }}
              >
                {c.initial}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-[13px] font-bold text-[#1f1f25]">{c.name}</p>
                  <span className="shrink-0 text-[11px] font-semibold text-[#1f1f25]/65">{c.time}</span>
                </div>
                <p className="mt-0.5 truncate text-[12px] text-[#1f1f25]/60">{c.preview}</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="rounded-md bg-[#eeecff] px-1.5 py-0.5 text-[10px] font-bold text-[#5f4bc9]">{c.app}</span>
                  {c.flagged && (
                    <span className="rounded-md bg-[#ffd9ef] px-1.5 py-0.5 text-[10px] font-bold text-[#b62f63]">numero sconosciuto</span>
                  )}
                  {c.unread > 0 && (
                    <span className="rounded-full bg-[#7763e5] px-1.5 text-[10px] font-black text-white">{c.unread}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        <p className="pt-1 text-center text-[11px] font-medium text-[#1f1f25]/65">
          Chat completa WhatsApp: anche i messaggi inviati e quelli eliminati.
        </p>
      </div>
    );

  if (id === "calls")
    return (
      <div className="space-y-2">
        {CALLS.map((c, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl border border-[#1f1f25]/8 bg-white p-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#eeecff]">
              <DirIcon dir={c.dir} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-bold text-[#1f1f25]">{c.name}</p>
              <p className="truncate text-[11px] text-[#1f1f25]/65">{c.number}</p>
            </div>
            <div className="text-right">
              <p className="text-[12px] font-bold text-[#1f1f25]/70">{c.dur}</p>
              <p className="text-[11px] text-[#1f1f25]/65">{c.time}</p>
            </div>
          </div>
        ))}
      </div>
    );

  if (id === "screen")
    return (
      <div className="space-y-2.5">
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="relative aspect-[9/16] overflow-hidden rounded-xl border border-[#1f1f25]/8 bg-gradient-to-br from-[#ece9fb] to-[#f6f0ff]">
              <div className="absolute inset-x-2 top-2 h-1.5 rounded bg-white/70" />
              <div className="absolute inset-x-2 top-5 space-y-1">
                <div className="h-1 rounded bg-white/60" />
                <div className="h-1 w-2/3 rounded bg-white/50" />
              </div>
              <span className="absolute bottom-1.5 left-0 right-0 text-center text-[10px] font-bold text-[#1f1f25]/65">
                {["14:30", "13:12", "12:40"][i]}
              </span>
              <span className="absolute left-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded bg-[#1f1f25]/70 text-white">
                <Camera size={9} />
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-[#1f1f25]/8 bg-white p-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#ffd9ef] text-[#b62f63]">
            <Radio size={16} />
          </span>
          <div className="flex-1">
            <p className="text-[13px] font-bold text-[#1f1f25]">Diretta fotocamera attiva</p>
            <p className="text-[11px] text-[#1f1f25]/65">Stream MJPEG · 8,1 fps · 240 frames</p>
          </div>
          <span className="flex items-center gap-1 rounded-full bg-[#ffd9ef] px-2 py-0.5 text-[10px] font-black text-[#b62f63]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#ffd9ef]-deep" /> LIVE
          </span>
        </div>
        <p className="text-center text-[11px] font-medium text-[#1f1f25]/65">
          Screenshot e foto camera: on-demand o schedulati.
        </p>
      </div>
    );

  if (id === "location")
    return (
      <div className="space-y-2.5">
        <div
          className="relative h-40 overflow-hidden rounded-2xl border border-[#1f1f25]/8 bg-[#EAF3EA]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(31,31,37,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(31,31,37,0.05) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        >
          <span className="absolute left-[24%] top-[30%] flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#7763e5] shadow">
            <MapPin size={14} />
          </span>
          <span className="absolute right-[20%] top-[24%] flex h-8 w-8 items-center justify-center rounded-full bg-white text-emerald-500 shadow">
            <Shield size={14} />
          </span>
          <span className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-[#7763e5] px-3 py-1 text-[10px] font-black text-white shadow-lg">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#ffd335]" /> Sofia è qui · 14:33
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-2xl border border-[#1f1f25]/8 bg-white p-3">
            <p className="text-[11px] font-semibold text-[#1f1f25]/65">Luogo sicuro</p>
            <p className="text-[13px] font-bold text-[#1f1f25]">Scuola · arrivato 07:58</p>
          </div>
          <div className="rounded-2xl border border-[#1f1f25]/8 bg-white p-3">
            <p className="text-[11px] font-semibold text-[#1f1f25]/65">Percorso oggi</p>
            <p className="text-[13px] font-bold text-[#1f1f25]">4,2 km · 3 soste</p>
          </div>
        </div>
      </div>
    );

  if (id === "web")
    return (
      <div className="space-y-2">
        {SITES.map((s, i) => (
          <div key={i} className={`flex items-center gap-3 rounded-2xl border bg-white p-3 ${s.ok ? "border-[#1f1f25]/8" : "border-[#e8558f]/40"}`}>
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${s.ok ? "bg-[#e7f8f3] text-emerald-600" : "bg-[#ffd9ef] text-[#b62f63]"}`}>
              <Globe size={15} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-bold text-[#1f1f25]">{s.url}</p>
              <p className="truncate text-[11px] text-[#1f1f25]/65">{s.title}</p>
            </div>
            <span className="shrink-0 text-[11px] font-bold text-[#1f1f25]/65">{s.dur}</span>
          </div>
        ))}
        <p className="pt-1 text-center text-[11px] font-medium text-[#1f1f25]/65">
          Multi-browser: Chrome, Edge, Samsung Internet, Opera, Firefox.
        </p>
      </div>
    );

  if (id === "apps")
    return (
      <div className="space-y-3">
        <div className="rounded-2xl border border-[#1f1f25]/8 bg-white p-3.5">
          <p className="text-[11px] font-semibold text-[#1f1f25]/65">Tempo schermo oggi</p>
          <p className="mt-0.5 font-display text-3xl font-black text-[#1f1f25]">2h 15m</p>
          <p className="mt-0.5 text-[11px] font-bold text-[#7763e5]">−20% rispetto a ieri</p>
        </div>
        {APPS.map((a) => (
          <div key={a.app}>
            <div className="flex items-center justify-between text-[12px] font-semibold text-[#1f1f25]/70">
              <span>{a.app}</span>
              <span>{a.time}</span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[#eeecff]/60">
              <div className="h-full rounded-full" style={{ width: a.w, backgroundColor: a.color }} />
            </div>
          </div>
        ))}
      </div>
    );

  if (id === "sms")
    return (
      <div className="space-y-2">
        {SMS.map((s, i) => (
          <div key={i} className="rounded-2xl border border-[#1f1f25]/8 bg-white p-3">
            <div className="flex items-center justify-between">
              <p className="text-[13px] font-bold text-[#1f1f25]">{s.from}</p>
              <span className="text-[11px] text-[#1f1f25]/65">{s.time}</span>
            </div>
            <p className="mt-1 text-[12px] leading-relaxed text-[#1f1f25]/70">{s.text}</p>
          </div>
        ))}
      </div>
    );

  if (id === "audio")
    return (
      <div className="space-y-2.5">
        <div className="rounded-2xl border border-[#1f1f25]/8 bg-white p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#e0f2fe] text-[#0284c7]">
              <Mic size={18} />
            </span>
            <div className="flex-1">
              <p className="text-[13px] font-bold text-[#1f1f25]">Registrazione ambientale</p>
              <p className="text-[11px] text-[#1f1f25]/65">14:00 · durata 2:00 · schedulata</p>
            </div>
          </div>
          <div className="mt-3 flex h-10 items-end gap-1">
            {[30, 55, 40, 70, 90, 60, 45, 75, 50, 35, 65, 85, 55, 40, 60, 30, 50, 70, 45, 60, 35, 55, 80, 40].map((h, i) => (
              <span key={i} className="flex-1 rounded-full bg-[#7763e5]/70" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-2xl bg-[#fff7de] p-3 text-[11px] font-medium text-[#1f1f25]/70">
          <Lock size={14} className="shrink-0 text-[#1f1f25]/65" />
          Audio in diretta: si attiva solo su comando del genitore (opt-in).
        </div>
      </div>
    );

  if (id === "commands")
    return (
      <div className="grid grid-cols-2 gap-2.5">
        {[
          { icon: Camera, label: "Foto camera", color: "bg-[#ffd9ef] text-[#b62f63]" },
          { icon: Monitor, label: "Screenshot", color: "bg-[#eeecff] text-[#7763e5]" },
          { icon: Mic, label: "Avvia audio", color: "bg-[#e0f2fe] text-[#0284c7]" },
          { icon: Radio, label: "Avvia diretta", color: "bg-[#e7f8f3] text-emerald-600" },
          { icon: MapPin, label: "Posizione ora", color: "bg-[#fff7de] text-[#1f1f25]" },
        ].map((c) => (
          <button key={c.label} type="button" className="cursor-pointer rounded-2xl border border-[#1f1f25]/8 bg-white p-3 text-left transition-transform hover:-translate-y-0.5">
            <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${c.color}`}>
              <c.icon size={17} />
            </span>
            <p className="mt-2.5 text-[12px] font-bold text-[#1f1f25]">{c.label}</p>
            <p className="text-[10px] font-medium text-[#1f1f25]/65">on-demand</p>
          </button>
        ))}
        <p className="col-span-2 pt-1 text-center text-[11px] font-medium text-[#1f1f25]/65">
          Ogni comando mostra la conferma di esecuzione in tempo reale.
        </p>
      </div>
    );

  return null;
};

export const DemoDashboard = () => {
  const [active, setActive] = useState("chat");

  if (!isVip) return null;

  const current = CATEGORIES.find((c) => c.id === active);

  return (
    <section id="demo" className="relative scroll-mt-24 overflow-hidden bg-[#1f1f25] py-24 text-white md:py-28">
      <div aria-hidden="true" className="absolute inset-0 bg-dots-light opacity-40" />
      <div
        aria-hidden="true"
        className="absolute -left-40 top-10 h-[420px] w-[420px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(119,99,229,0.35), transparent)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(232,85,143,0.22), transparent)" }}
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#ffd335]/15 px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-[#ffd335]">
            <Monitor size={14} /> Demo dashboard
          </span>
          <h2 className="mt-5 font-display text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">
            Guarda <span className="text-[#ffd335]">esattamente</span> cosa vedi nel pannello VIP
          </h2>
          <p className="mt-4 text-base font-medium leading-relaxed text-white/70 sm:text-lg">
            Questa è una riproduzione fedele della dashboard, con dati dimostrativi. Tocca una
            categoria a sinistra per vedere cosa intercetta ogni funzione.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] shadow-2xl shadow-black/40 backdrop-blur"
        >
          {/* Topbar dispositivo */}
          <div className="flex flex-wrap items-center gap-3 border-b border-white/10 bg-white/[0.03] px-4 py-3 sm:px-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7763e5] text-white">
              <Shield size={20} />
            </span>
            <div className="mr-auto">
              <p className="flex items-center gap-2 text-sm font-black">
                Guardian Family VIP
                <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> LIVE · CONNESSO
                </span>
              </p>
              <p className="text-[11px] text-white/45">Dashboard genitore · demo</p>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-white/55">
              <span className="hidden items-center gap-1.5 sm:flex">
                <Smartphone size={13} /> {DEVICE.child}
              </span>
              <span className="hidden items-center gap-1.5 md:flex">
                <Wifi size={13} /> online
              </span>
              <span className="flex items-center gap-1.5">
                <Battery size={13} /> {DEVICE.battery}%
              </span>
              <span className="hidden items-center gap-1.5 sm:flex">
                <Clock size={13} /> {DEVICE.lastSeen}
              </span>
            </div>
          </div>

          <div className="grid lg:grid-cols-[240px_1fr]">
            {/* Sidebar categorie */}
            <nav className="border-b border-white/10 p-3 lg:border-b-0 lg:border-r" aria-label="Categorie dati monitorati">
              <ul className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
                {CATEGORIES.map((c) => {
                  const on = c.id === active;
                  return (
                    <li key={c.id} className="shrink-0 lg:shrink">
                      <button
                        type="button"
                        onClick={() => setActive(c.id)}
                        aria-pressed={on}
                        data-testid={`demo-cat-${c.id}`}
                        className={`flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors ${
                          on ? "bg-[#7763e5] text-white shadow-lg shadow-[#7763e5]/30" : "text-white/60 hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <c.icon size={17} className="shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] font-bold">{c.label}</span>
                          <span className={`block truncate text-[10px] ${on ? "text-white/60" : "text-white/35"}`}>{c.sub}</span>
                        </span>
                        {c.count != null && (
                          <span className={`shrink-0 rounded-full px-1.5 text-[10px] font-black ${on ? "bg-white text-[#7763e5]" : "bg-white/10 text-white/60"}`}>
                            {c.count}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Pannello attivo */}
            <div className="bg-[#FBFAF7] p-4 text-[#1f1f25] sm:p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#7763e5] text-white">
                    {current && <current.icon size={18} />}
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-black text-[#1f1f25]">{current?.label}</h3>
                    <p className="text-[11px] font-medium text-[#1f1f25]/65">{current?.sub}</p>
                  </div>
                </div>
                <span className="rounded-full bg-[#eeecff] px-3 py-1 text-[11px] font-bold text-[#7763e5]">
                  {DEVICE.device}
                </span>
              </div>

              <motion.div
                key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28 }}
              >
                <Panel id={active} />
              </motion.div>
            </div>
          </div>

          <div className="border-t border-white/10 bg-white/[0.03] px-4 py-3 text-center sm:px-6">
            <p className="text-[11px] font-medium text-white/45">
              Anteprima illustrativa della dashboard VIP · dati di esempio, non appartengono a
              nessun utente reale
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
